/**
 * Client-side pending checkout resume (Cosmos / prototype UX).
 * Persist orderId when user leaves checkout during QR / awaiting payment;
 * clear on paid / failed / cancelled.
 */

export const PENDING_CHECKOUT_KEY = 'kiosk.pendingCheckout';

export interface PendingCheckout {
  orderId: string;
  provider?: string | null;
  amount?: number;
  method?: string | null;
  createdAt: number;
  resumePath: string;
  /** Payment surface data so resume can re-show the QR/status without creating a new order. */
  paymentUrl?: string;
  qrCode?: string;
  address?: string;
  expiresAt?: string;
  orderNumber?: string;
}

export function savePendingCheckout(data: Omit<PendingCheckout, 'createdAt'> & { createdAt?: number }): void {
  try {
    const payload: PendingCheckout = {
      ...data,
      createdAt: data.createdAt ?? Date.now(),
    };
    localStorage.setItem(PENDING_CHECKOUT_KEY, JSON.stringify(payload));
  } catch (err) {
    console.warn('[pendingCheckout] save failed', err);
  }
}

export function getPendingCheckout(): PendingCheckout | null {
  try {
    const raw = localStorage.getItem(PENDING_CHECKOUT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingCheckout;
    if (!parsed?.orderId || !parsed?.resumePath) return null;
    // Expire after 30 minutes (matches pending-transaction window) or explicit expiresAt
    const isPastExpiry = parsed.expiresAt ? new Date(parsed.expiresAt).getTime() <= Date.now() : false;
    if (Date.now() - (parsed.createdAt || 0) > 30 * 60 * 1000 || isPastExpiry) {
      clearPendingCheckout();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearPendingCheckout(): void {
  try {
    localStorage.removeItem(PENDING_CHECKOUT_KEY);
  } catch {
    /* ignore */
  }
}
